import { BadgeCheck, Siren, HeartHandshake, Cross, Layers, UserCheck } from "lucide-react";
import { PageHero, SectionHeading, Reveal, CTABand, usePageMeta } from "@/components/kit";
import { HERO_BG, SLOGAN, PHONE } from "@/lib/site";

const REASONS = [
  { icon: BadgeCheck, t: "ISNetworld Member Contractor", d: "Our safety, insurance, and quality practices are verified through ISNetworld — the same credential platform trusted by major industrial and commercial clients. When you hire us, you're hiring a vetted professional." },
  { icon: Siren, t: "24/7 After-Hours Emergency Service", d: `A cut fiber line or failed camera system doesn't wait for Monday morning. Call ${PHONE} after hours or on weekends and reach real emergency dispatch — not a voicemail box.` },
  { icon: HeartHandshake, t: "Special Rates for Community Institutions", d: "Churches, schools, and emergency personnel facilities receive preferred pricing. Keeping the places that serve our community running is a conviction, not a marketing line." },
  { icon: Cross, t: "Faith-Driven Accountability", d: "We answer to something higher than a quarterly review. That shows up as honest quotes, kept appointments, tidy work areas, and repairs we actually stand behind." },
  { icon: Layers, t: "One Team, Every Low-Voltage System", d: "Cameras, access control, fiber, Ethernet, and intercoms all connect. One accountable crew services the whole ecosystem — no finger-pointing between companies." },
  { icon: UserCheck, t: "Owner-Operated Attention", d: "You deal directly with the person whose name is on the company. Every estimate, every service call, every final test gets an owner's eyes." },
];

export default function WhyUsPage() {
  usePageMeta(
    "Why Choose Us | Redeemer Solutions LLC",
    "ISNetworld member contractor, 24/7 emergency service, special rates for churches and schools, and faith-driven integrity. Why Middle Tennessee trusts Redeemer Solutions LLC."
  );
  return (
    <div data-testid="why-us-page">
      <PageHero
        testid="why-us-hero"
        eyebrow="Why Choose Us"
        lines={["The Difference Is", { t: "in the Details", gold: true }]}
        sub="Plenty of companies sell new systems. Fewer will answer at 9 PM on a Saturday to fix the one you already own, test every run, and treat your budget like their own. That's the Redeemer difference."
        image={HERO_BG}
        imageAlt="Professional low-voltage technician at work in dramatic lighting"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
        <SectionHeading
          eyebrow="Six Reasons"
          title="Why Middle Tennessee calls Redeemer."
          testid="why-us-reasons-heading"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {REASONS.map((r, i) => (
            <Reveal key={r.t} delay={i * 0.07}>
              <div data-testid={`reason-card-${i}`} className="bg-[#121216] border border-gold/15 rounded-sm p-7 sm:p-8 gold-glow-card h-full">
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 bg-gold/5">
                  <r.icon size={22} className="text-gold" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#F4EFE6] mt-5 leading-snug">{r.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{r.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="isnetworld-band" className="border-y border-gold/15 bg-[#0D0D10] relative overflow-hidden">
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-3 border border-gold/40 bg-gold/5 rounded-sm px-6 py-5">
              <BadgeCheck size={36} className="text-gold shrink-0" />
              <div>
                <p className="font-display text-lg font-bold text-gold-champagne tracking-wide">ISNetworld</p>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.22em] text-zinc-400 mt-0.5">Member Contractor</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-zinc-300 text-base leading-relaxed">
              ISNetworld membership means our safety record, insurance, and business practices are continuously verified
              against the standards of the world's leading industrial and commercial hiring clients. It's a credential
              that has to be maintained — and we maintain it gladly.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand title={SLOGAN} sub="Experience the difference on your next service call. Fast dispatch — call or request your quote today." />
    </div>
  );
}
